import React, { useCallback, useEffect, useState } from "react";
import { MdAdd, MdClose, MdEdit, MdRestore, MdSubdirectoryArrowRight, MdVisibilityOff } from "react-icons/md";
import { toast } from "react-toastify";
import Modal from "../../../../components/modal";
import { TicketCategory, TicketQueue } from "../../../../interfaces/Ticket";
import {
  createTicketCategory,
  deactivateTicketCategory,
  getTicketCategories,
  reactivateTicketCategory,
  updateTicketCategory,
} from "../../../../services/ticketApi";

interface Props {
  open: boolean;
  queues: TicketQueue[];
  onClose: () => void;
  /** Avisa a tela de chamados para recarregar o catálogo que ela usa nos formulários. */
  onChanged: () => void;
}

/** O que está sendo editado. `parentId` fixo em "nova subcategoria"; livre em "nova categoria". */
interface Rascunho {
  id: number | null;
  parentId: number | null;
  name: string;
  defaultQueueId: number | null;
}

const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-gray-600 dark:bg-navy-900 dark:text-white";

/**
 * ⚠ `Editor` e `Linha` vivem no escopo do MÓDULO, e não dentro de `CategoriesModal`.
 *
 * Declarados dentro, eles trocariam de identidade a cada render — e React desmonta e remonta a
 * subárvore quando o tipo do elemento muda. O efeito visível seria o pior possível: o `<input>` do
 * editor perderia o foco **a cada tecla digitada**, porque cada `setRascunho` re-renderiza o modal.
 */
const Editor: React.FC<{
  titulo: string;
  rascunho: Rascunho;
  queues: TicketQueue[];
  salvando: boolean;
  onChange: (r: Rascunho) => void;
  onCancel: () => void;
  onSave: () => void;
}> = ({ titulo, rascunho, queues, salvando, onChange, onCancel, onSave }) => (
  <div className="mb-4 rounded-xl border border-dv-blue-200 bg-dv-blue-50/50 p-3 dark:border-dv-blue-500/30 dark:bg-dv-blue-500/10">
    <p className="mb-2 text-xs font-bold uppercase tracking-wide text-dv-blue-700 dark:text-dv-blue-300">
      {titulo}
    </p>
    <div className="flex flex-col gap-2 sm:flex-row">
      <input
        autoFocus
        className={inputCls}
        placeholder="Nome da categoria"
        value={rascunho.name}
        onChange={(e) => onChange({ ...rascunho, name: e.target.value })}
        onKeyDown={(e) => e.key === "Enter" && onSave()}
      />
      <select
        className={inputCls}
        aria-label="Fila padrão"
        value={rascunho.defaultQueueId ?? ""}
        onChange={(e) =>
          onChange({ ...rascunho, defaultQueueId: e.target.value === "" ? null : Number(e.target.value) })
        }
      >
        <option value="">— Sem fila própria —</option>
        {queues.map((q) => (
          <option key={q.id} value={q.id}>
            {q.name}
          </option>
        ))}
      </select>
    </div>
    <div className="mt-2 flex justify-end gap-2">
      <button
        type="button"
        onClick={onCancel}
        className="rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-white dark:text-gray-300 dark:hover:bg-navy-700"
      >
        Cancelar
      </button>
      <button
        type="button"
        disabled={salvando || rascunho.name.trim().length < 2}
        onClick={onSave}
        className="rounded-lg bg-ink-900 px-4 py-1.5 text-xs font-bold text-white disabled:opacity-50 dark:bg-white dark:text-ink-900"
      >
        Salvar
      </button>
    </div>
  </div>
);

const Linha: React.FC<{
  c: TicketCategory;
  filha: boolean;
  onEdit: (r: Rascunho) => void;
  onNewChild: (parentId: number) => void;
  onDeactivate: (id: number) => void;
  onReactivate: (id: number) => void;
}> = ({ c, filha, onEdit, onNewChild, onDeactivate, onReactivate }) => (
  <div
    className={`flex items-center gap-2 border-b border-gray-50 py-2 dark:border-white/5 ${
      filha ? "pl-7" : ""
    } ${c.flActive ? "" : "opacity-50"}`}
  >
    {filha && <MdSubdirectoryArrowRight className="h-4 w-4 shrink-0 text-gray-300" aria-hidden="true" />}

    <div className="min-w-0 flex-1">
      <p
        className={`truncate text-sm ${
          filha ? "text-gray-700 dark:text-gray-300" : "font-semibold text-navy-700 dark:text-white"
        }`}
      >
        {c.name}
        {!c.flActive && <span className="ml-2 text-[11px] font-normal italic text-gray-400">desativada</span>}
      </p>
      <p className="truncate text-[11px] text-gray-500 dark:text-gray-400">
        {c.effectiveQueueName ? (
          <>
            {/* Herdada × própria: sem essa distinção o admin conclui que a subcategoria está sem
                roteamento quando ela só está herdando o da raiz. */}
            Fila: {c.effectiveQueueName}
            {c.defaultQueueId == null && " (herdada)"}
          </>
        ) : (
          "Sem roteamento — vai para triagem"
        )}
        {c.openTicketCount > 0 && ` · ${c.openTicketCount} em aberto`}
      </p>
    </div>

    {!filha && c.flActive && (
      <button
        type="button"
        title="Nova subcategoria"
        onClick={() => onNewChild(c.id)}
        className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-navy-700 dark:hover:bg-navy-700 dark:hover:text-white"
      >
        <MdAdd className="h-4 w-4" aria-hidden="true" />
      </button>
    )}

    <button
      type="button"
      title="Editar"
      onClick={() =>
        onEdit({ id: c.id, parentId: c.parentId ?? null, name: c.name, defaultQueueId: c.defaultQueueId ?? null })
      }
      className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-navy-700 dark:hover:bg-navy-700 dark:hover:text-white"
    >
      <MdEdit className="h-4 w-4" aria-hidden="true" />
    </button>

    {c.flActive ? (
      <button
        type="button"
        title={c.parentId == null ? "Desativar (desativa as subcategorias junto)" : "Desativar"}
        onClick={() => onDeactivate(c.id)}
        className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
      >
        <MdVisibilityOff className="h-4 w-4" aria-hidden="true" />
      </button>
    ) : (
      <button
        type="button"
        title="Reativar"
        onClick={() => onReactivate(c.id)}
        className="rounded-lg p-1.5 text-gray-400 hover:bg-green-50 hover:text-green-600 dark:hover:bg-green-900/20"
      >
        <MdRestore className="h-4 w-4" aria-hidden="true" />
      </button>
    )}
  </div>
);

/**
 * Administração do catálogo de categorias (`tickets.admin`).
 *
 * Modal e não rota própria: é configuração de um módulo, mexida raramente e sempre por quem já está
 * na tela de Chamados — uma rota a mais no menu custaria mais atenção do que o uso justifica. Mesma
 * escolha do `BoardColumnsModal`.
 */
const CategoriesModal: React.FC<Props> = ({ open, queues, onClose, onChanged }) => {
  const [categories, setCategories] = useState<TicketCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [mostrarInativas, setMostrarInativas] = useState(false);
  const [rascunho, setRascunho] = useState<Rascunho | null>(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      // `includeInactive` sempre: a tela precisa ver o desativado para poder reativar. O filtro
      // visual é local — pedir de novo ao servidor a cada clique no checkbox seria uma ida à rede
      // para esconder linhas que já estão na mão.
      setCategories(await getTicketCategories(true));
    } catch {
      toast.error("Não foi possível carregar as categorias.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) void carregar();
  }, [open, carregar]);

  if (!open) return null;

  const executar = async (acao: () => Promise<unknown>, sucesso: string) => {
    setSalvando(true);
    try {
      await acao();
      toast.success(sucesso);
      setRascunho(null);
      await carregar();
      onChanged();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Não foi possível salvar.");
    } finally {
      setSalvando(false);
    }
  };

  const salvar = () => {
    if (!rascunho || rascunho.name.trim().length < 2) return;

    const body = {
      parentId: rascunho.parentId,
      name: rascunho.name.trim(),
      defaultQueueId: rascunho.defaultQueueId,
    };

    return rascunho.id == null
      ? executar(() => createTicketCategory(body), "Categoria criada.")
      : executar(() => updateTicketCategory(rascunho.id!, body), "Categoria atualizada.");
  };

  const visiveis = mostrarInativas ? categories : categories.filter((c) => c.flActive);

  const acoesDaLinha = {
    onEdit: setRascunho,
    onNewChild: (parentId: number) =>
      setRascunho({ id: null, parentId, name: "", defaultQueueId: null }),
    onDeactivate: (id: number) =>
      void executar(() => deactivateTicketCategory(id), "Categoria desativada."),
    onReactivate: (id: number) =>
      void executar(() => reactivateTicketCategory(id), "Categoria reativada."),
  };

  return (
    <Modal onClose={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[20px] border border-gray-100 bg-white p-6 shadow-dv-lg dark:border-white/10 dark:bg-navy-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">Categorias de chamado</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="rounded-lg p-1 text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-700"
          >
            <MdClose className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
          Dois níveis. A fila padrão roteia o chamado quando quem abre não escolhe uma — a
          subcategoria herda a fila da categoria principal quando não tem a sua.
        </p>

        <div className="mb-3 flex items-center justify-between gap-3">
          <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={mostrarInativas}
              onChange={(e) => setMostrarInativas(e.target.checked)}
              className="h-3.5 w-3.5 rounded"
            />
            Mostrar desativadas
          </label>
          <button
            type="button"
            onClick={() => setRascunho({ id: null, parentId: null, name: "", defaultQueueId: null })}
            className="flex items-center gap-1.5 rounded-xl bg-ink-900 px-4 py-2 text-xs font-bold text-white hover:bg-ink-800 dark:bg-white dark:text-ink-900"
          >
            <MdAdd className="h-4 w-4" aria-hidden="true" />
            Nova categoria
          </button>
        </div>

        {rascunho && (
          <Editor
            titulo={
              rascunho.id != null
                ? "Editando"
                : rascunho.parentId != null
                ? "Nova subcategoria"
                : "Nova categoria principal"
            }
            rascunho={rascunho}
            queues={queues}
            salvando={salvando}
            onChange={setRascunho}
            onCancel={() => setRascunho(null)}
            onSave={() => void salvar()}
          />
        )}

        {loading ? (
          <p className="py-8 text-center text-sm text-gray-500">Carregando…</p>
        ) : visiveis.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">Nenhuma categoria cadastrada.</p>
        ) : (
          <div>
            {visiveis.map((raiz) => (
              <React.Fragment key={raiz.id}>
                <Linha c={raiz} filha={false} {...acoesDaLinha} />
                {(mostrarInativas ? raiz.children : raiz.children.filter((f) => f.flActive)).map((f) => (
                  <Linha key={f.id} c={f} filha {...acoesDaLinha} />
                ))}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CategoriesModal;
